/**
 * Fiação em React da máquina pura de `bridge-onboarding.ts` — o ÚNICO lugar deste
 * app que chama `detectSigningProvider`/`listProviderCerts` a partir de um timer.
 * `BridgePanel`/`SignPage` só leem `state` e chamam `retry`/`startDownload`.
 *
 * ## Garantias do loop (§9)
 *
 * - **Timeout individual por chamada**: `detectSigningProvider()` → `detectBridge()`
 *   já limita CADA chamada nativa com o `withTimeout` de `bridge.ts` (D-113) — este
 *   hook não escreve um segundo timeout, só chama uma função que já não pendura.
 * - **Loop único**: `loopId` é a "geração" do loop corrente. Todo callback agendado
 *   (timer ou promise em voo) confere `myLoop === loopId.current` antes de agir; se
 *   não bater, é um resto de um loop já cancelado e não faz nada. Um novo poll só é
 *   agendado depois que o anterior TERMINOU (a promise resolveu) — nunca em paralelo.
 * - **Cancelamento**: sair da cerimônia (`active` vira `false`) ou desmontar incrementa
 *   `loopId` e limpa o timer pendente — nenhum efeito tardio muda o estado depois disso.
 * - **Termina em `READY`**: fora das fases `WAITING_FOR_INSTALL`/`CONNECTING`
 *   (`isPollingPhase`), nenhum novo timer de detecção é agendado.
 * - **Janela até `RECOVERABLE_ERROR`**: checada a cada resposta de poll contra
 *   `hasPollTimedOut`/`POLL_TIMEOUT_WINDOW_MS` — sem relógio próprio, sem duplicar
 *   `setTimeout` de vigia.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { describeError } from './errors';
import { detectSigningProvider, listProviderCerts, type SigningProviderStatus } from './signing-provider';
import {
  BACKGROUND_POLL_INTERVAL_MS,
  hasPollTimedOut,
  initialBridgeOnboardingState,
  isPollingPhase,
  nextPollDelayMs,
  reduceBridgeOnboarding,
  type BridgeOnboardingState,
} from './bridge-onboarding';

export interface UseBridgeOnboardingResult {
  state: BridgeOnboardingState;
  /** "Verificar novamente" — nunca abre sessão nem mexe no envio (§12); só
   * redescobre o provider e, se pronto, relista certificados. */
  retry: () => void;
  /** "Baixar (novamente) o ARGINK Bridge" — liga (ou reinicia) o polling automático
   * pós-download (§9). */
  startDownload: () => void;
}

/**
 * @param active Só corre efeito (checagem inicial, polling, listagem de
 *   certificados) enquanto `true` — no signer-ui, "a etapa de certificado da
 *   Qualificada está em tela". Desligar `active` cancela qualquer chamada em voo
 *   sem descartar o `state` já conhecido (reativar não refaz uma descoberta boa).
 */
export function useBridgeOnboarding(active: boolean): UseBridgeOnboardingResult {
  const [state, dispatch] = useReducer(reduceBridgeOnboarding, initialBridgeOnboardingState);

  const loopId = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearScheduled = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Cancelamento: dispara ao DESLIGAR `active` (cleanup roda ANTES do efeito
  // re-rodar com o novo valor) e ao desmontar. Uma detecção ou listagem já em voo
  // ainda resolve, mas encontra `myLoop !== loopId.current` e não despacha nada.
  useEffect(() => {
    return () => {
      loopId.current += 1;
      clearScheduled();
    };
  }, [active, clearScheduled]);

  /**
   * Toda detecção TEM de despachar, mesmo quando falha.
   *
   * `detectSigningProvider` promete assentar sempre, mas ela atravessa a API MV3 legada — código de
   * terceiro no MAIN world — e uma chamada síncrona lá dentro (`isExtensionPresent`, `version`) pode
   * lançar antes de qualquer `withTimeout`. Sem `catch` aqui, a promise rejeitava, NENHUM `dispatch`
   * acontecia, a fase ficava em `CHECKING` — que não é fase de polling, então nada reagendava — e a
   * tela dizia "Procurando seus certificados neste computador…" para sempre. Uma extensão legada
   * meio carregada bastava para pendurar a etapa inteira. (Achado P1 do Terra, 09/09/2026.)
   *
   * Falha de detecção é indistinguível, para o usuário, de "não achei o Bridge" — então é isso que
   * despachamos: o loop segue vivo e a próxima tentativa acontece.
   */
  const runDetection = useCallback((myLoop: number) => {
    detectSigningProvider()
      .catch((): SigningProviderStatus => ({ kind: 'bridge_absent' }))
      .then((status) => {
        if (myLoop !== loopId.current) return;
        dispatch({ type: 'PROVIDER_DETECTED', status, now: Date.now() });
      });
  }, []);

  // O CORAÇÃO do loop: lê a fase ATUAL e decide a única próxima ação — nunca duas
  // agendadas ao mesmo tempo, porque cada execução deste efeito primeiro desfaz
  // (via cleanup) o que a execução anterior tinha agendado.
  useEffect(() => {
    if (!active) return;
    const myLoop = loopId.current;

    if (state.phase === 'CHECKING') {
      runDetection(myLoop);
      return;
    }

    if (isPollingPhase(state.phase)) {
      if (hasPollTimedOut(state.waitStartedAt, Date.now())) {
        dispatch({ type: 'POLL_TIMED_OUT' });
        return;
      }
      timerRef.current = setTimeout(() => {
        if (myLoop !== loopId.current) return;
        runDetection(myLoop);
      }, nextPollDelayMs(state.pollAttempt));
      return clearScheduled;
    }

    if (state.phase === 'READY') {
      dispatch({ type: 'CERTIFICATES_REQUESTED' });
      return;
    }

    if (state.phase === 'DISCOVERING_CERTIFICATES' && state.provider) {
      const provider = state.provider.kind;
      listProviderCerts(provider).then(
        (certs) => {
          if (myLoop !== loopId.current) return;
          dispatch({ type: 'CERTIFICATES_LOADED', certs });
        },
        (err: unknown) => {
          if (myLoop !== loopId.current) return;
          const human = describeError(err);
          dispatch({ type: 'CERTIFICATES_FAILED', detail: human.detail, traceId: human.traceId });
        },
      );
      return;
    }

    // Polling "de fundo" (§9): a tela já está em RECOVERABLE_ERROR por timeout de
    // instalação, mas continua olhando, mais devagar — se a instalação terminar,
    // a tela se recupera sozinha, sem exigir clique.
    if (state.phase === 'RECOVERABLE_ERROR' && state.error?.reason === 'poll_timeout') {
      timerRef.current = setTimeout(() => {
        if (myLoop !== loopId.current) return;
        runDetection(myLoop);
      }, BACKGROUND_POLL_INTERVAL_MS);
      return clearScheduled;
    }

    // NOT_INSTALLED, INCOMPATIBLE, CERTIFICATES_READY, NO_COMPATIBLE_CERTIFICATE,
    // RECOVERABLE_ERROR(certificates_failed): estáveis — esperam ação do usuário
    // (`retry`/`startDownload`), sem nenhum timer.
    return undefined;
  }, [active, state.phase, state.pollAttempt, state.waitStartedAt, state.provider, state.error, runDetection, clearScheduled]);

  // Ação MANUAL (clique) durante uma detecção automática em voo é o único jeito de
  // duas chamadas coexistirem sem estarem na mesma "geração": a resposta antiga
  // teria opinião sobre uma fase que o clique já mudou. Invalidar o `loopId` aqui
  // garante que ela chegue tarde e não faça nada — a fase seguinte parte de um
  // loop novo, iniciado limpo pelo efeito acima.
  const retry = useCallback(() => {
    loopId.current += 1;
    dispatch({ type: 'RETRY_REQUESTED', now: Date.now() });
  }, []);

  const startDownload = useCallback(() => {
    loopId.current += 1;
    dispatch({ type: 'DOWNLOAD_STARTED', now: Date.now() });
  }, []);

  return { state, retry, startDownload };
}
